import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useHistory } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { debounce } from 'lodash';
import { bindActionCreators } from 'redux';
import { useSelector, useDispatch } from 'react-redux';

import * as actions from 'containers/Projects/actions';
import { SET_PROJECT } from 'containers/Projects/types';
import { pushSiteNotification as actionPushSiteNotification } from 'containers/App/actions';
import ProjectModal from 'components/Modal/Project';
import APModal from 'components/Modal/APModal';
import DeleteConfirmation from 'components/DeleteConfirmation';
import { SET_PROJECTS_VIEW_OPTIONS } from 'containers/App/constants';
import ProjectsTable from './Projects.Table';
import EmptyPage from './Projects.Empty';

const PAGE_SIZE = 12;

const Projects = forwardRef(({ options, searchKey, showProjectModal, isActive }, ref) => {
  const history = useHistory();
  const [settingsWeddingId, setSettingsWeddingId] = useState(null);
  const [settingsWeddingChanges, setSettingsWeddingChanges] = useState(null);
  const [openProjectModal, setOpenProjectModal] = useState(false);
  const [showArchiveConfirm, setArchiveConfirm] = useState(false);
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [openProjectsLimitErrorModal, setOpenProjectsLimitErrorModal] = useState(false);
  const [openUnarchiveLimitErrorModal, setOpenUnarchiveLimitErrorModal] = useState(false);
  const [сonfirmProjectId, setConfirmProjectId] = useState(null);

  const [showDeleteConfirm, setDeleteConfirm] = useState(false);
  const [showUnarchiveConfirm, setUnarchiveConfirm] = useState(false);

  const userInfo = useSelector((state) => state.global.userInfo);
  const isProjectsLoaded = useSelector((state) => state.projects.isProjectsLoaded);
  const { orgId: organizationId, organization } = userInfo || {};
  const isOrgOwner = organization?.ownerId === userInfo?.id;

  const currentProjects = isActive ? useSelector((state) => state.projects.activeProjects)
  : useSelector((state) => state.projects.archivedProjects);

  const searchedProjects = useSelector((state) => state.projects.searchedProjects);
  const { hasMore, currentPage, projects } = searchKey !== '' ? searchedProjects : currentProjects;

  const filteredProjects = isActive ? projects : projects.filter(p => p.isOwner);

  const projectsLimit = useSelector((state) => state.projects.projectsLimit);

  const dispatch = useDispatch();

  const {
    getInitialProjects,
    getProjects,
    archiveProject,
    deleteProject,
    resetSearchResults,
    pushNotification,
  } = bindActionCreators({ ...actions,  pushNotification: actionPushSiteNotification }, dispatch);

  const archiveAction = (e) => {
    if (e) {
      e.stopPropagation();
      archiveProject({ id: e.currentTarget.dataset.id, action: 'archive' });
    }
    setArchiveConfirm(true);
  };

  const settings = (e) => {
    e.stopPropagation();
    setSettingsWeddingId(e.currentTarget.dataset.id);
    setSettingsWeddingChanges(null);
    setOpenProjectModal(true);
  };

  const unarchiveAction = (e) => {
    if (e) {
      e.stopPropagation();
      // Display error modal for unarchiving if max projects is reached.
      const i = currentProjects.projects.findIndex(el => el.weddingId === parseInt(e.currentTarget.dataset.id, 10));
      const current = currentProjects.projects[i];
      if (projectsLimit.limit && projectsLimit.activeWeddingCount >= projectsLimit.limit && !current.isSample) {
        setOpenUnarchiveLimitErrorModal(true);
      } else {
        archiveProject({ id: e.currentTarget.dataset.id, action: 'unarchive' });
        setUnarchiveConfirm(true);
      }
    }
  };

  const deleteAction = (e) => {
    e.stopPropagation();
    setConfirmProjectId(e.currentTarget.dataset.id);
    setDeleteConfirm(true);
  };

  useImperativeHandle(ref, () => ({
    updateList: (optionLeadId) => {
      const sort = isActive ? options.sort : options.sortArchive;
      updateWeddingsList(optionLeadId, organizationId, searchKey, !isActive, sort);
    },
  }));

  // When searching or changing sorts.
  const updateWeddingsList = async (leadId, orgId, keyS, isArchived, sortKey) => {
    const payload = {
      page: 0,
      pageSize: PAGE_SIZE,
      // #TODO: fix the backend search so we don't have to send org id.
      orgId: keyS ? orgId : null,
      isArchived,
      leadId: isArchived ? 0 : Number(leadId),
      key: keyS,
      sortKey,
    };
    getProjects(payload);
  };

  const getPayload = (pageNumber = 0) => {

    return {
      page: pageNumber,
      pageSize: PAGE_SIZE,
      isArchived: !isActive,
      leadId: options?.filter === 'lead-name' && isActive ? Number(options.leadId) : 0,
      key: searchKey,
      sortKey: isActive ? options.sort : options.sortArchive,
    };

  };

  const delayedQuery = useCallback(
    debounce((id, orgId, q, isArchived, sortKey) => {
      updateWeddingsList(id, orgId, q, isArchived, sortKey);
    }, 500)
  , []);

  // Initial load
  useEffect(() => {
    getInitialProjects({ ...getPayload(), sortArchiveKey: options.sortArchive });
  }, []);

  useEffect(() => {
    if (isProjectsLoaded) {
      if (searchKey === '') {
        resetSearchResults();
      }
      delayedQuery(
        options?.filter === 'lead-name' ? Number(options.leadId) : 0,
        organizationId,
        searchKey ?? '',
        !isActive,
        isActive ? options.sort : options.sortArchive,
      );
    }
  }, [searchKey]);

  // Infinite scroll fetch.
  const fetchData = () => {
    getProjects(getPayload(currentPage));
  };

  // When changing options.
  useEffect(() => {
    if (isProjectsLoaded)
      getProjects(getPayload());
  }, [options]);

  const updateWeddingChanges = (wedding) => {
    setSettingsWeddingChanges(wedding);
  };

  const updateProjectChanges = (changes) => {
    if (changes !== null) {
      const projectStatus = searchKey !== '' ? 'searched' : isActive ? 'active' : 'archived';

      dispatch({
        type: SET_PROJECT,
        payload: { projectChanges: changes },
        projectStatus,
      });
    }
  };

  useEffect(() => {
    if (!openProjectModal) {
      updateProjectChanges(settingsWeddingChanges);
    }
  }, [settingsWeddingChanges]);

  const goToProject = async (id) => {
    if (isActive && projectsLimit.limit && projectsLimit.activeWeddingCount > projectsLimit.limit) {
      setOpenProjectsLimitErrorModal(true);
    } else if (id) {
      window.location = `/app/project/${id}/dashboard`;
    }
  };

  useEffect(() => {
    if (currentProjects.currentPage && isActive) {
      if (currentProjects.projects.length === 0) {
        // If there is no projects by the lead id filter, we should
        // change filter to 'show-all'
        if (options.filter !== 'lead-name') {
          setShowLandingPage(true);
        } else {
          dispatch({
            type: SET_PROJECTS_VIEW_OPTIONS,
            payload: { ...options, filter: 'show-all', leadId: 0 },
          });
        }
      } else {
        setShowLandingPage(false);
      }
    } else {
      setShowLandingPage(false);
    }
  }, [currentProjects]);

  const projectsLimitErrorAction = () => {
    return (
      <button
        className="button"
        onClick={() => setOpenProjectsLimitErrorModal(false)}
      >
        Got it
      </button>
    );
  };

  const projectsUnarchiveLimitErrorAction = () => {
    if (isOrgOwner) {
      return (
        <button
          className="button"
          onClick={() => {
            history.push('/app/v2/account/billing');
          }}
        >
          Upgrade Subscription
        </button>
      );
    }

    return (
      <button
        className="button"
        onClick={() => setOpenUnarchiveLimitErrorModal(false)}
      >
        Got it
      </button>
    );
  };

  const projectArchiveAction = () => {
    return (
      <button
        className="button"
        onClick={() => setArchiveConfirm(false)}
      >
        Got it
      </button>
    );
  };

  const projectUnarchiveAction = () => {
    return (
      <button
        className="button"
        onClick={() => setUnarchiveConfirm(false)}
      >
        Got it
      </button>
    );
  };

  const renderEmptyProjects = () => (
    <div className="project-index__empty">There are currently no projects.</div>
  );

  return (
    <>
      <APModal
        open={openProjectsLimitErrorModal}
        onClose={() => setOpenProjectsLimitErrorModal(false)}
        name="projects-limit-error"
        title="Heads up!"
        actions={projectsLimitErrorAction()}
      >
        <div className="content-container content-container--narrow">
          <div>It looks like you have more active projects than your current subscription supports.
            To access your projects, please archive any projects over your plan limit or upgrade to a larger plan.
          </div>
        </div>
      </APModal>
      <APModal
        open={openUnarchiveLimitErrorModal}
        onClose={() => setOpenUnarchiveLimitErrorModal(false)}
        name="projects-unarchive-limit-error"
        title="Heads up!"
        actions={projectsUnarchiveLimitErrorAction()}
      >
        <div className="content-container content-container--narrow">
          <div>
            {isOrgOwner && (
              <>
                Your current subscription limits you to { projectsLimit.limit } projects.
                To unarchive additional projects, upgrade your plan.
              </>
            )}
            {!isOrgOwner && (
              <>To unarchive additional projects, please contact the owner of your account to upgrade your plan.</>
            )}
          </div>
        </div>
      </APModal>
      {showLandingPage && isActive ? (
        <EmptyPage showProjectModal={showProjectModal} />
      ) : (filteredProjects?.length || searchedProjects?.length ? (
        <InfiniteScroll
          dataLength={projects.length}
          next={fetchData}
          hasMore={hasMore}
        >
          <ProjectsTable
            // Bust render cache when sorting and view changes.
            key={`project-index-${options.view}-${options.sort}-${options.sortArchive}`}
            options={options}
            projects={searchedProjects?.length ? searchedProjects : filteredProjects}
            action1={isActive ? settings : unarchiveAction}
            name1={isActive ? 'Settings' : 'Unarchive'}
            action2={isActive ? archiveAction : deleteAction}
            name2={isActive ? 'Archive' : 'Delete'}
            goToProject={goToProject}
            isActive={isActive}
          />
        </InfiniteScroll>
      ) : (
        searchKey && renderEmptyProjects()
      )
      )}
      <ProjectModal
        open={openProjectModal}
        onClose={() => {
          setOpenProjectModal(false);
          updateProjectChanges(settingsWeddingChanges);
          setSettingsWeddingChanges(null);
          }
        }
        weddingId={settingsWeddingId}
        updateWeddingChanges={updateWeddingChanges}
        pushNotification={ pushNotification }
      />
      <APModal
        open={showArchiveConfirm}
        onClose={() => setArchiveConfirm(false)}
        name="project-action-confirm"
        title="Project Archived."
        actions={projectArchiveAction()}
      >
        <div className="content-container content-container--narrow">
          <div>
            This project has now been archived. Planning partners outside of
            your organization will no longer have access, and any associated
            items have been removed from your calendar. You can still view this
            project in read-only mode from the archive tab.
          </div>
        </div>
      </APModal>
      <APModal
        open={showUnarchiveConfirm}
        onClose={() => setUnarchiveConfirm(false)}
        name="project-action-confirm"
        title="Project Unarchived."
        actions={projectUnarchiveAction()}
      >
        <div className="content-container content-container--narrow">
          <div>
            This project has been successfully unarchived. Client access and
            calendar items are now restored, and the project is available in
            your Active Projects tab.
          </div>
        </div>
      </APModal>
      <DeleteConfirmation
        show={showDeleteConfirm}
        handleClose={() => {
          setConfirmProjectId(null);
          setDeleteConfirm(false);
        }}
        handleConfirm={() => {
        deleteProject({ id: сonfirmProjectId });
        setConfirmProjectId(null);
        setDeleteConfirm(false);
        }}
        deleteText="DELETE PROJECT"
        size="xs"
      >
        <p>Deleting this project will remove it from your account forever. Are you sure?</p>
      </DeleteConfirmation>
    </>
  );
});

export default Projects;
